import { Injectable, signal } from '@angular/core';
import {
  BehaviorSubject,
  catchError,
  map,
  Observable,
  of,
  switchMap,
  tap,
} from 'rxjs';
import { TranslationRequest } from '../../shared/models/translation/translation-request';
import { SelectOption } from '../../shared';
import { AzureTranslationRequest } from '../../shared/models/translation/azure-translation-request';
import { HttpClient } from '@angular/common/http';
import { ConfigurationService, Session } from 'src/app/my-sedgwick';

/**
 * Interface for all cache keys
 */
interface ICacheKey {
  getCacheKeyString(): string;
  equals(other: ICacheKey): boolean;
}

/**
 * Cache key for translations based on language, group, and subgroup
 */
class TranslationsCacheKey implements ICacheKey {
  constructor(
    public readonly language: string,
    public readonly group: string = '',
    public readonly subGroup: string = ''
  ) {}

  getCacheKeyString(): string {
    return `translations|lang:${this.language}|group:${this.group}|subgroup:${this.subGroup}`;
  }

  equals(other: ICacheKey): boolean {
    if (!other || !(other instanceof TranslationsCacheKey)) {
      return false;
    }
    return this.getCacheKeyString() === other.getCacheKeyString();
  }

  toString(): string {
    return this.getCacheKeyString();
  }
}

/**
 * Cache key for languages based on contract number
 */
class LanguagesCacheKey implements ICacheKey {
  constructor(public readonly contNum: number) {}

  getCacheKeyString(): string {
    return `languages|contNum:${this.contNum}`;
  }

  equals(other: ICacheKey): boolean {
    if (!other || !(other instanceof LanguagesCacheKey)) {
      return false;
    }
    return this.getCacheKeyString() === other.getCacheKeyString();
  }

  toString(): string {
    return this.getCacheKeyString();
  }
}

/**
 * Generic cache value that can store any data with expiration
 */
class CacheValue<T> {
  private cacheTime: Date;
  private readonly cacheExpiry: number; // in milliseconds

  constructor(
    public data: T,
    cacheExpiryMinutes: number = 15
  ) {
    this.cacheTime = new Date();
    this.cacheExpiry = cacheExpiryMinutes * 60 * 1000;
  }

  isExpired(): boolean {
    return (new Date().getTime() - this.cacheTime.getTime()) > this.cacheExpiry;
  }

}

/**
 * Generic cache manager for client-side caching
 */
class ClientCacheManager {
  private static instance: ClientCacheManager;
  private cache = new Map<string, CacheValue<any>>();

  static getInstance(): ClientCacheManager {
    if (!ClientCacheManager.instance) {
      ClientCacheManager.instance = new ClientCacheManager();
    }
    return ClientCacheManager.instance;
  }


  get<T>(cacheKey: ICacheKey): T | null {
    const keyString = cacheKey.getCacheKeyString();
    const cachedValue = this.cache.get(keyString) as CacheValue<T>;
    
    if (cachedValue && !cachedValue.isExpired()) {
      return cachedValue.data;
    }
    return null;
  }

  set<T>(cacheKey: ICacheKey, data: T, cacheExpiryMinutes: number = 15): void {
    const keyString = cacheKey.getCacheKeyString();
    this.cache.set(keyString, new CacheValue(data, cacheExpiryMinutes));
  }

  clear(cacheKey: ICacheKey): void {
    const keyString = cacheKey.getCacheKeyString();
    this.cache.delete(keyString);
  }

  clearAll(): void {
    this.cache.clear();
  }
}


@Injectable({ providedIn: 'root' })
export class TranslationService {
  private cacheManager = ClientCacheManager.getInstance();
  private currentCacheKey: TranslationsCacheKey | null = null;
  /** Prevents duplicate AzureTranslate calls for the same key while a request is in flight. */
  private pendingAzureTranslations = new Set<string>();
  
  languages = signal<SelectOption[]>([]);
  translations: Map<string, string> = new Map();
  private translationsSubject = new BehaviorSubject<Map<string, string>>(
    new Map()
  );
  public translations$ = this.translationsSubject.asObservable();
  
  constructor( private httpClient: HttpClient, public session: Session,
      public config: ConfigurationService) {
  }

  /**
   * Save translations to cache using cache key system
   */
  private saveTranslationsToCache(translationRequest: TranslationRequest): void {
    const currentLanguage = sessionStorage.getItem('languagepreference');
    if (!currentLanguage || currentLanguage === 'en' || this.translations.size === 0) {
      return;
    }

    // Save to cache manager
    if(translationRequest?.saveToCache){
        // Create cache key for this specific translation request
        const cacheKey = new TranslationsCacheKey(
          currentLanguage,
          translationRequest?.group || '',
          translationRequest?.subGroup || ''
        );
        this.cacheManager.set(cacheKey, this.translations, 15); // 15 minutes expiry
        this.currentCacheKey = cacheKey;
    }
  }

  /**
   * Clear cached translations
   */
  public clearTranslationCache(): void {
    if (this.currentCacheKey) {
      this.cacheManager.clear(this.currentCacheKey);
    }
    this.cacheManager.clearAll(); // Clear all translation caches
    this.currentCacheKey = null;
    this.translations.clear();
    this.translationsSubject.next(this.translations);
  }

  isLanguagePreferenceUnset(): boolean {
    const langPref = sessionStorage.getItem('languagepreference');
    return (
      langPref === null ||
      langPref === undefined ||
      langPref === '' ||
      langPref === 'en'
    );
  }

  /**
   * Check if translations are already loaded for current request (language, group, subgroup)
   */
  private hasTranslationsForCurrentRequest(translationRequest: TranslationRequest): boolean {

    if (this.isLanguagePreferenceUnset()) {
      return false;
    }

    const currentLanguage = sessionStorage.getItem('languagepreference');
    const cacheKey = new TranslationsCacheKey(
      currentLanguage,
      translationRequest?.group || '',
      translationRequest?.subGroup || ''
    );

    const cachedTranslations = this.cacheManager.get<Map<string, string>>(cacheKey);
    return cachedTranslations !== null && cachedTranslations.size > 0;
  }

  /**
   * Get Azure translation for a given key
   * Backend automatically logs missing keys asynchronously
   * @param key The translation key that needs to be translated
   * @param translationRequest Additional context information for logging
   * @returns Observable of the translated text from Azure or original key if translation fails
   */
  private getAzureTranslation(key: string, translationRequest: TranslationRequest, isHtml: boolean = false): Observable<string> {
    if(!translationRequest) {
      return of(key);
    }

    if(key == null || key == undefined || key.trim() == '' || key == ' ') {
      return of(key);
    }
    
    if(translationRequest.language == null || translationRequest.language == undefined || translationRequest.language == '') {
      return of(key);
    }
    
    const sanitizedKey = isHtml ? this.sanitizeForXss(key) : key;

    const azureTranslationRequest: AzureTranslationRequest = {
      text: sanitizedKey,
      targetLanguage: translationRequest.language,
      sourceLanguage: 'en',
      group: translationRequest.group,
      subGroup: translationRequest.subGroup,
    };

    return this.httpClient.post<string>('/api/Translation/AzureTranslate', azureTranslationRequest).pipe(
      map(translatedText => {
        // Return the translated text or original key if translation failed
        return translatedText && translatedText.trim() !== '' ? translatedText : key;
      }),
      catchError(error => {
        return of(key);
      })
    );
  }

  isLanguagePreferenceSet(): boolean {
    const langPref = sessionStorage.getItem('languagepreference');
    // If not set, treat as not set (return false)
    if (langPref === null || langPref === undefined || langPref === '' || langPref === 'null' || langPref === 'undefined') {
      return false;
    }

    if (langPref === 'en') {
      return false;
    }
    // Otherwise, language preference is set
    return true;
  }

  async getTranslation(translationRequest: TranslationRequest): Promise<void> {
    if(!this.isLanguagePreferenceSet()) {
      this.translations = new Map();
      this.translationsSubject.next(this.translations);
      return;
    }

    if(this.translations.size > 0 && translationRequest.group == 'Shared' && translationRequest.subGroup == 'Shared') {
      return;
    }

    // Check if we already have translations for the current request (language, group, subgroup)
    if (this.hasTranslationsForCurrentRequest(translationRequest)) {
      const currentLanguage = sessionStorage.getItem('languagepreference');
      const cacheKey = new TranslationsCacheKey(
        currentLanguage,
        translationRequest?.group || '',
        translationRequest?.subGroup || ''
      );
      
      const cachedTranslations = this.cacheManager.get<Map<string, string>>(cacheKey);
      if (cachedTranslations) {
        this.translations = cachedTranslations;
        this.translationsSubject.next(this.translations);
        this.currentCacheKey = cacheKey;
        return;
      }
    }

    translationRequest.contNum = Number(this.session.user.contNum);
    this.translations = new Map();
    this.translationsSubject.next(this.translations);

    try {
      const translationsObj = await this.httpClient
        .post<{ [key: string]: string }>(
          '/api/Translation/GetTranslations',
          translationRequest
        )
        .toPromise();
      
      this.translations = new Map(Object.entries(translationsObj));
      this.translationsSubject.next(this.translations);
      
      // Save to cache for future use
      this.saveTranslationsToCache(translationRequest);
    } catch (error) {
    }
  }

  async mergeTranslationsFromRequest(
    translationRequest: TranslationRequest
  ): Promise<void> {
    if (!this.isLanguagePreferenceSet()) {
      return;
    }

    translationRequest.contNum = Number(this.session.user.contNum);

    try {
      const translationsObj = await this.httpClient
        .post<{ [key: string]: string }>(
          '/api/Translation/GetTranslations',
          translationRequest
        )
        .toPromise();

      if (!translationsObj) {
        return;
      }

      for (const [k, v] of Object.entries(translationsObj)) {
        this.translations.set(k, v);
      }
      this.translationsSubject.next(this.translations);

      if (translationRequest?.saveToCache) {
        this.saveTranslationsToCache(translationRequest);
      }
    } catch {
    }
  }

  /* Non-Observable version of getTranslationForKey - Called from translation pipe
     Gets the translation for a key from the translations map
     @param key The translation key
     @param translationRequest The translation request
     @returns The translation for the key
     */
  getTranslationForKey(
    key: string,
    translationRequest: TranslationRequest = null,
    isHtml: boolean = false,
    disableAutoTranslation: boolean = false
  ): string {
    if(!this.translations || this.translations.size === 0) {
      return key;
    }

    if(key == null || key == undefined || key.trim() == '' || key == ' ') {
        return (key); // Return original key if no key
    }

    if (((!this.translations.has(key) && !this.translations.has(key.trim())) || (this.translations.get(key) == null || this.translations.get(key) == undefined || this.translations.get(key) == ''))) {

      if (disableAutoTranslation == true) {
        return key;
      }

      // Deduplicate: template getters / impure pipes can call this every change detection.
      // Also always cache the Azure result (even when text is unchanged, e.g. Spanish "Error")
      // so we do not retry forever.
      const pendingKey = this.getAzurePendingKey(key, translationRequest);
      if (!this.pendingAzureTranslations.has(pendingKey)) {
        this.pendingAzureTranslations.add(pendingKey);
        this.getAzureTranslation(key, translationRequest, isHtml).subscribe({
          next: (translatedText) => {
            const value =
              translatedText && translatedText.trim() !== ''
                ? this.decodeHtmlEntities(translatedText)
                : key;
            this.translations.set(key, value);
            this.pendingAzureTranslations.delete(pendingKey);
          },
          error: () => {
            this.translations.set(key, key);
            this.pendingAzureTranslations.delete(pendingKey);
          },
        });
      }
      return key;
    }

    // Try to get translation with the key, fallback to trimmed key
    return this.decodeHtmlEntities(
      this.translations.get(key) || this.translations.get(key.trim())
    );
  }

  /* Observable version of getTranslationForKey - Called from translation pipe
     Gets the translation for a key from the translations map
     @param key The translation key
     @param translationRequest The translation request
     @returns The translation for the key
     */
  getTranslationForKey$(
    key: string,
    translationRequest: TranslationRequest = null,
    disableAutoTranslation: boolean = false
  ): Observable<string> {
    return this.translations$.pipe(
      switchMap(translations => {
        if(!translations || translations.size === 0) {
          return of(key);
        }

        if(key == null || key == undefined || key.trim() == '' || key == ' ') {
          return of(key); // Return original key if no key
        }

        if ((!this.translations.has(key) && !this.translations.has(key.trim())) || this.translations.get(key) == null){

          if (disableAutoTranslation == true) {
            return of(key);
          }

          // Return the translated text from Azure if available
          if(translationRequest != null) {
            translationRequest.group = 'Shared';
            translationRequest.subGroup = 'Shared';
          }

          const pendingKey = this.getAzurePendingKey(key, translationRequest);
          if (this.pendingAzureTranslations.has(pendingKey)) {
            return of(key);
          }

          this.pendingAzureTranslations.add(pendingKey);
          return this.getAzureTranslation(key, translationRequest).pipe(
            tap(translatedText => {
              const value =
                translatedText && translatedText.trim() !== ''
                  ? this.decodeHtmlEntities(translatedText)
                  : key;
              this.translations.set(key, value);
              this.pendingAzureTranslations.delete(pendingKey);
            }),
            map(translatedText =>
              translatedText && translatedText.trim() !== ''
                ? this.decodeHtmlEntities(translatedText)
                : key
            ),
            catchError(() => {
              this.translations.set(key, key);
              this.pendingAzureTranslations.delete(pendingKey);
              return of(key);
            })
          );
        }

        // Try to get translation with the key, fallback to trimmed key
        return of(
          this.decodeHtmlEntities(
            this.translations.get(key) || this.translations.get(key.trim()) || key
          )
        );
      })
    );
  }

  private getAzurePendingKey(key: string, translationRequest: TranslationRequest = null): string {
    return [
      key,
      translationRequest?.language || '',
      translationRequest?.group || '',
      translationRequest?.subGroup || '',
    ].join('|');
  }

  /* Non-Observable version of getTranslationForKeyClientCustomization - Called from client customization pipe
     Gets the translation for a key from the translations map
     @param key The translation key
     @param valueForClientSpecific The value for client specific
     @returns The translation for the key
     */
  getTranslationForKeyClientCustomization(
    key: string,
    valueForClientSpecific: string = '',
    translationRequest: TranslationRequest = null,
    disableAutoTranslation: boolean = false
  ): string {
    if (valueForClientSpecific === '') {
      return null;
    }
    let translatedValue = this.getTranslationForKey(key, translationRequest, false, disableAutoTranslation);
    if (translatedValue === key) {
      return valueForClientSpecific;
    }
    return translatedValue;
  }

  /* Observable version of getTranslationForKeyClientCustomization - Called from client customization pipe
     Gets the translation for a key from the translations map
     @param key The translation key
     @param valueForClientSpecific The value for client specific
     @returns The translation for the key
     */
  getTranslationForKeyClientCustomization$(
    key: string,
    valueForClientSpecific: string = '',
    translationRequest: TranslationRequest = null,
    disableAutoTranslation: boolean = false
  ): Observable<string> {
    if (valueForClientSpecific === '') {
      return this.translations$.pipe(map(() : string => null));
    }

    return this.getTranslationForKey$(key, translationRequest, disableAutoTranslation).pipe(
      map((translatedValue) => {
        return translatedValue === key ? valueForClientSpecific : translatedValue;
      })
    );
  }

  /**
   * Gets available languages from the API with caching
   * @returns Observable of key-value pairs representing language codes and names
   */
  getLanguages(contNum: string): Observable<{[key: string]: string}> {
    const cacheKey = new LanguagesCacheKey(Number(contNum));
    
    // Check cache first
    const cachedLanguages = this.cacheManager.get<{[key: string]: string}>(cacheKey);
    if (cachedLanguages) {
      return of(cachedLanguages);
    }

    // Load from API and cache the result
    return this.httpClient.get<{[key: string]: string}>('/api/Translation/GetLanguages').pipe(
      tap(languages => {
        // Cache the languages for 15 minutes
        this.cacheManager.set(cacheKey, languages, 15);
      }),
      catchError(error => {
        return of({});
      })
    );
  }

  async getLanguagesAsync() {
    if (!!this.languages().length) return;
    const contNum = this.session.user.contNum;
    const response = await this.getLanguages(contNum).toPromise();

    const languages: SelectOption[] = Object.entries(response).map(
      ([value, label]) => ({
        value,
        label,
      })
    );

    languages.sort((a, b) => a.value.localeCompare(b.value));

    this.languages.set(languages);
  }

  async isMultiLanagueFeatureEnabled(): Promise<boolean> {
    // Wait for config to be ready before checking the feature
    const isEnabled = await this.config.getConfigFeatureAccessAsync('MultiLanguageTranslation');
    return isEnabled;
  }

  async setDefaultLanguage(langaugeCode: string): Promise<void> {
    const isEnabled = await this.isMultiLanagueFeatureEnabled();
    if(isEnabled){
      sessionStorage.setItem('languagepreference', langaugeCode);
    }
    else{
      sessionStorage.removeItem('languagepreference');
    }
  }

  async getDefaultLangauge(): Promise<string> {
    if (this.session != null && this.session != undefined) {
      const isEnabled = await this.isMultiLanagueFeatureEnabled();
      if (!isEnabled) {
        return this.getBrowserLangauge();
      }
      else {
        if (this.isLanguagePreferenceSet()) {
          return sessionStorage.getItem('languagepreference');
        }
        else {
          const defaultLang = this.session.user.defaultLanguageCode?.length > 1 ? this.session.user.defaultLanguageCode : this.getBrowserLangauge();
          return defaultLang;
        }
      }
    }
    else {
      return this.getBrowserLangauge();
    }
  }

  getBrowserLangauge(): string {
    let browserLanguage = navigator.language || navigator.languages?.[0] || 'en';
    browserLanguage = browserLanguage.toLowerCase().startsWith('en') ? 'en' : browserLanguage.toLowerCase().substring(0, 2);
    return browserLanguage.toString();
  }

  /**
   * Decode HTML entities (&#233; → é, &#8217; → ’) without stripping tags.
   * Translations stored/returned as entities render as literal codes in {{ }} bindings.
   */
  private decodeHtmlEntities(text: string): string {
    if (!text || text.indexOf('&') === -1) {
      return text;
    }

    let decoded = text
      .replace(/&amp;/g, '&')
      .replace(/&#(\d+);/g, (_, code) => String.fromCharCode(Number(code)))
      .replace(/&#x([0-9a-fA-F]+);/g, (_, hex) =>
        String.fromCharCode(parseInt(hex, 16))
      )
      .replace(/&apos;/g, "'")
      .replace(/&quot;/g, '"')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&nbsp;/g, '\u00A0');

    // Second pass for double-encoded values (&amp;#233; → &#233; → é)
    if (decoded.indexOf('&#') !== -1 || decoded.indexOf('&amp;') !== -1) {
      decoded = decoded
        .replace(/&amp;/g, '&')
        .replace(/&#(\d+);/g, (_, code) => String.fromCharCode(Number(code)))
        .replace(/&#x([0-9a-fA-F]+);/g, (_, hex) =>
          String.fromCharCode(parseInt(hex, 16))
        );
    }

    return decoded;
  }

  /**
   * Sanitize input for XSS using the browser's DOM parser,
   * mirroring the server-side AntiXssMiddleware (Ganss.Xss.HtmlSanitizer).
   * Parses HTML through the DOM to strip dangerous elements/attributes,
   * then extracts the safe text content.
   */
  private sanitizeForXss(input: string): string {
    if (!input) {
      return input;
    }
    const parser = new DOMParser();
    const doc = parser.parseFromString(input, 'text/html');
    const sanitized = doc.body.textContent || '';
    return sanitized.replace(/\u00A0/g, ' ');
  }
}
